// Teile der Zeiterfassung im Widget-Stil (design_handoff_personal §3, Screens 01–05): Tages-Tabelle (auf dem Handy
// Karten), Antrags-Karten für Mehrarbeit und Korrekturen. Nur Darstellung — Handler kommen aus ZeiterfassungSeite.
import type { ReactNode } from 'react';
import {
  Check, Clock, ClockCountdown, Confetti, PencilSimple, Plus, PlusCircle, Trash, X, type Icon,
} from '@phosphor-icons/react';
import { stundenText, tagText, tagVon, uhrzeit, type Stempel, type Tageszeile } from '../../../../lib/zeiterfassung/berechnung';
import { STATUS_INFO, TYP_INFO } from '../../../../lib/zeiterfassung/abwesenheit';
import { antragStatus, farbeAusName, korrekturArt, saldoTonTag, tagAbzeichen, TON_FARBE } from '../../../../lib/personalAnsicht/darstellung';
import type { Korrektur, UeberstundenAntrag } from '../../../../lib/zeiterfassung/api';
import { TYP_ICON } from './symbole';

const SPALTEN = '64px minmax(0,1fr) 64px 82px 82px 96px';
const SPALTEN_MOBIL = '44px minmax(0,1fr) auto';

function Chip({ icon: I, children, farbe, flaeche, tip }: { icon?: Icon; children: ReactNode; farbe: string; flaeche: string; tip?: string }) {
  return (
    <span data-tip={tip} className="inline-flex max-w-full items-center" style={{ height: 26, padding: '0 10px', gap: 5, borderRadius: 13, fontSize: 11.5, fontWeight: 800, background: flaeche, color: farbe }}>
      {I && <I size={12} weight="fill" className="shrink-0" />}<span className="truncate">{children}</span>
    </span>
  );
}

export function TagesTabelle({ zeilen, heute, mobil, laedt, onNachtragen, onBearbeiten, onLoeschen }: {
  zeilen: readonly Tageszeile[]; heute: string; mobil: boolean; laedt: boolean;
  onNachtragen: (tag: string) => void; onBearbeiten: (s: Stempel) => void; onLoeschen: (s: Stempel) => void;
}) {
  const spalten = mobil ? SPALTEN_MOBIL : SPALTEN;
  return (
    <section className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 8 }} aria-label="Arbeitszeit je Tag">
      {laedt ? (
        <div className="flex justify-center" style={{ padding: '60px 0' }}><span className="animate-spin" style={{ width: 22, height: 22, borderRadius: '50%', border: '2.5px solid var(--fill3)', borderTopColor: 'var(--acc)' }} /></div>
      ) : zeilen.length === 0 ? (
        <div className="flex flex-col items-center" style={{ padding: 36, gap: 8, color: 'var(--ink3)' }}>
          <Clock size={28} /><span style={{ fontSize: 14, fontWeight: 600 }}>Keine Zeiten in diesem Monat</span>
        </div>
      ) : (
        <>
          {!mobil && (
            <div className="grid" style={{ gridTemplateColumns: spalten, gap: 12, padding: '10px 12px 8px', fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
              <span>Tag</span><span>Buchungen</span><span className="text-right">Pause</span><span className="text-right">Ist</span><span className="text-right">Soll</span><span className="text-right">Saldo</span>
            </div>
          )}
          {zeilen.map((z) => {
            const istHeute = z.tag === heute;
            const abz = tagAbzeichen(z.tag);
            const sTon = TON_FARBE[saldoTonTag(z.saldoMinuten)];
            const pause = z.pauseMinuten > 0 ? `${z.pauseMinuten} min` : '—';
            const abw = z.abwesenheit;
            const AbwIcon = abw ? TYP_ICON[abw.typ] : undefined;
            const abwFarbe = abw ? farbeAusName(TYP_INFO[abw.typ].farbe) : null;
            return (
              <div key={z.tag} className="immo-zeile-hover grid items-center"
                style={{ gridTemplateColumns: spalten, gap: 12, padding: '8px 12px', borderRadius: 18, background: istHeute || z.laufend ? 'var(--acc-soft)' : 'transparent' }}>
                <span aria-label={tagText(z.tag)} className="inline-flex flex-col items-center justify-center"
                  style={{ width: 44, height: 46, borderRadius: 14, background: istHeute ? 'var(--acc-fill)' : 'var(--fill2)', color: istHeute ? 'var(--acc-ink)' : 'var(--ink)',
                    boxShadow: istHeute ? 'var(--acc-sh)' : 'none' }}>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em' }}>{abz.wochentag}</span>
                  <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1 }}>{abz.zahl}</span>
                </span>
                <div className="flex min-w-0 flex-wrap items-center" style={{ gap: 6 }}>
                  {z.feiertag && <Chip icon={Confetti} farbe="var(--info)" flaeche="var(--info-soft)" tip="Feiertag – Kein Soll an diesem Tag">{z.feiertag}</Chip>}
                  {abw && abwFarbe && (
                    <Chip icon={AbwIcon} farbe={abwFarbe.farbe} flaeche={abwFarbe.flaeche} tip={`${TYP_INFO[abw.typ].label} – ${STATUS_INFO[abw.status].label}`}>
                      {TYP_INFO[abw.typ].label}{abw.status !== 'genehmigt' ? ' (offen)' : ''}{abw.halber_tag ? ' ½' : ''}
                    </Chip>
                  )}
                  {z.stempel.length === 0 && !z.feiertag && !abw && (
                    <button type="button" onClick={() => onNachtragen(z.tag)} data-tip="Nachtragen – Für diesen Tag fehlt eine Buchung"
                      className="immo-tippziel inline-flex items-center" style={{ height: mobil ? 44 : 28, padding: '0 12px', gap: 5, borderRadius: 14, border: '1.5px dashed var(--line)', cursor: 'pointer',
                        fontFamily: 'inherit', fontSize: 12, fontWeight: 700, background: 'transparent', color: 'var(--acc)' }}>
                      <Plus size={11} weight="bold" />nachtragen
                    </button>
                  )}
                  {z.stempel.map((s) => (
                    <span key={s.id} className="inline-flex max-w-full items-center" style={{ height: 30, padding: '0 4px 0 11px', gap: 6, borderRadius: 15, background: 'var(--fill2)', fontSize: 12.5, fontWeight: 700 }}>
                      {!s.ende && <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ok)', boxShadow: '0 0 0 3px var(--ok-soft)' }} />}
                      <span className="whitespace-nowrap tabular-nums">{uhrzeit(s.beginn)}–{s.ende ? uhrzeit(s.ende) : 'läuft'}</span>
                      {s.quelle === 'manuell' && (
                        <span data-tip="Nachgetragen – Diese Zeit wurde von Hand erfasst" style={{ height: 18, padding: '0 7px', borderRadius: 9, fontSize: 10, fontWeight: 800, lineHeight: '18px', background: 'var(--warn-soft)', color: 'var(--warn)' }}>nachgetragen</span>
                      )}
                      {s.notiz && <span className="truncate" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink3)' }}>· {s.notiz}</span>}
                      <button type="button" onClick={() => onBearbeiten(s)} aria-label="Bearbeiten" data-tip="Bearbeiten – Zeit korrigieren"
                        className="immo-tippziel inline-flex shrink-0 items-center justify-center" style={{ width: mobil ? 36 : 24, height: mobil ? 36 : 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--ink3)' }}>
                        <PencilSimple size={12} weight="bold" />
                      </button>
                      <button type="button" onClick={() => onLoeschen(s)} aria-label="Löschen" data-tip="Löschen – Buchung entfernen"
                        className="immo-tippziel inline-flex shrink-0 items-center justify-center" style={{ width: mobil ? 36 : 24, height: mobil ? 36 : 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--ink3)' }}>
                        <Trash size={12} weight="bold" />
                      </button>
                    </span>
                  ))}
                  {mobil && <span className="tabular-nums" style={{ width: '100%', fontSize: 11.5, color: 'var(--ink3)' }}>Pause {pause} · Ist {stundenText(z.istMinuten)} · Soll {stundenText(z.sollMinuten)}</span>}
                </div>
                {!mobil && (
                  <>
                    <span data-tip="Pause" className="text-right tabular-nums" style={{ fontSize: 13, color: 'var(--ink3)' }}>{pause}</span>
                    <span className="text-right tabular-nums" style={{ fontSize: 13.5, fontWeight: 700 }}>{stundenText(z.istMinuten)}</span>
                    <span className="text-right tabular-nums" style={{ fontSize: 13, color: 'var(--ink2)' }}>{stundenText(z.sollMinuten)}</span>
                  </>
                )}
                <span className="inline-flex items-center whitespace-nowrap tabular-nums" style={{ justifySelf: 'end', height: 26, padding: '0 10px', borderRadius: 13, fontSize: 12, fontWeight: 800, background: sTon.flaeche, color: sTon.farbe }}>
                  {stundenText(z.saldoMinuten, true)}
                </span>
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}

const STATUS_FARBE = { ok: 'emerald', warn: 'amber', bad: 'red', neutral: 'gray' } as const;

function AntragsKarte({ icon: I, farbe, flaeche, art, name, wann, text, status, aktionen }: {
  icon: Icon; farbe: string; flaeche: string; art: string; name?: string; wann: string; text: ReactNode; status: string; aktionen?: ReactNode;
}) {
  const s = antragStatus(status);
  const sf = farbeAusName(STATUS_FARBE[s.ton]);
  return (
    <div className="immo-glas flex flex-wrap items-center" style={{ gap: 14, padding: '14px 16px', borderRadius: 24 }}>
      <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 44, height: 44, borderRadius: 14, background: flaeche, color: farbe }}><I size={21} weight="fill" /></span>
      <div className="flex min-w-0 flex-col" style={{ flex: '1 1 240px', gap: 3 }}>
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 800 }}>{art}</span>
          {name && <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink2)' }}>{name}</span>}
          <span className="tabular-nums" style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink2)' }}>{wann}</span>
        </div>
        <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{text}</span>
      </div>
      <span className="inline-flex items-center" style={{ height: 26, padding: '0 10px', borderRadius: 13, fontSize: 11.5, fontWeight: 800, background: sf.flaeche, color: sf.farbe }}>{s.label}</span>
      {aktionen}
    </div>
  );
}

function EntscheidKnoepfe({ okLabel, okTip, onOk, onNein, mobil }: { okLabel: string; okTip: string; onOk: () => void; onNein: () => void; mobil: boolean }) {
  const h = mobil ? 44 : 36;
  return (
    <div className="flex" style={{ gap: 6 }}>
      <button type="button" onClick={onOk} data-tip={okTip} className="immo-tippziel inline-flex items-center"
        style={{ height: h, padding: '0 14px', gap: 6, borderRadius: h / 2, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800, background: 'var(--ok)', color: '#fff' }}>
        <Check size={13} weight="bold" />{okLabel}
      </button>
      <button type="button" onClick={onNein} data-tip="Ablehnen – Antrag nicht übernehmen" className="immo-tippziel inline-flex items-center"
        style={{ height: h, padding: '0 14px', gap: 6, borderRadius: h / 2, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800, background: 'var(--bad-soft)', color: 'var(--bad)' }}>
        <X size={13} weight="bold" />Ablehnen
      </button>
    </div>
  );
}

export function AntraegeListe({ mehrarbeit, korrekturen, verwalten, meineId, name, mobil, onMehrarbeit, onKorrektur, onZurueckziehen }: {
  mehrarbeit: readonly UeberstundenAntrag[]; korrekturen: readonly Korrektur[]; verwalten: boolean; meineId: string; name: (id: string) => string; mobil: boolean;
  onMehrarbeit: (m: UeberstundenAntrag, status: 'genehmigt' | 'abgelehnt') => void;
  onKorrektur: (k: Korrektur, status: 'genehmigt' | 'abgelehnt') => void;
  onZurueckziehen: (k: Korrektur) => void;
}) {
  if (mehrarbeit.length === 0 && korrekturen.length === 0) {
    return (
      <div className="immo-glas flex flex-col items-center" style={{ padding: 36, gap: 8, borderRadius: 26, color: 'var(--ink3)' }}>
        <Clock size={28} /><span style={{ fontSize: 14, fontWeight: 600 }}>Keine Korrekturanträge</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {/* Mehrarbeits-Anträge zuerst — sie betreffen künftige Stunden */}
      {mehrarbeit.map((m) => (
        <AntragsKarte key={m.id} icon={ClockCountdown} farbe="var(--pur)" flaeche="var(--pur-soft)" art="Mehrarbeit"
          name={verwalten ? name(m.mitarbeiter_id) : undefined} wann={`${tagText(m.datum)} · ${stundenText(m.minuten)}`} text={m.grund} status={m.status}
          aktionen={m.status === 'offen' && verwalten ? (
            <EntscheidKnoepfe okLabel="Genehmigen" okTip="Genehmigen – Mehrarbeit freigeben" mobil={mobil}
              onOk={() => onMehrarbeit(m, 'genehmigt')} onNein={() => onMehrarbeit(m, 'abgelehnt')} />
          ) : undefined} />
      ))}
      {korrekturen.map((k) => (
        <AntragsKarte key={k.id} icon={k.art === 'neu' ? PlusCircle : k.art === 'aendern' ? PencilSimple : Trash} farbe="var(--acc)" flaeche="var(--acc-soft)"
          art={korrekturArt(k.art)} name={verwalten ? name(k.mitarbeiter_id) : undefined}
          wann={k.beginn ? `${tagText(tagVon(k.beginn))} ${uhrzeit(k.beginn)}–${uhrzeit(k.ende)}${k.pause_minuten > 0 ? ` · ${k.pause_minuten} min Pause` : ''}` : '—'}
          text={<>{k.begruendung}{k.entscheidung_kommentar && <span style={{ fontStyle: 'italic', color: 'var(--ink3)' }}> · {k.entscheidung_kommentar}</span>}</>}
          status={k.status}
          aktionen={k.status === 'offen' && verwalten ? (
            <EntscheidKnoepfe okLabel="Übernehmen" okTip="Übernehmen – Korrektur ins Stundenkonto buchen" mobil={mobil}
              onOk={() => onKorrektur(k, 'genehmigt')} onNein={() => onKorrektur(k, 'abgelehnt')} />
          ) : k.status === 'offen' && !verwalten && k.mitarbeiter_id === meineId ? (
            <button className="immo-tippziel" type="button" onClick={() => onZurueckziehen(k)} data-tip="Zurückziehen – Eigenen Antrag zurücknehmen"
              style={{ height: mobil ? 44 : 36, padding: '0 14px', borderRadius: 18, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink)' }}>
              Zurückziehen
            </button>
          ) : undefined} />
      ))}
    </div>
  );
}
